import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/layout/Header';
import MobileNav from '@/components/layout/MobileNav';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { UserPlus, Copy, Check, Users, Crown, Loader2, Eye, MessageSquare, Heart, X, Mail, Flag, Trees, Share2, MessageCircle, Settings2, ImageDown, Pencil } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function FamilyPage() {
  const { user } = useAuth();
  const [family, setFamily] = useState(null);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteAccessLevel, setInviteAccessLevel] = useState('can_comment');
  const [inviting, setInviting] = useState(false);
  const [lastInviteLink, setLastInviteLink] = useState('');
  const [lastInviteEmailSent, setLastInviteEmailSent] = useState(false);
  const [copied, setCopied] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [newFamilyName, setNewFamilyName] = useState('');
  const [renaming, setRenaming] = useState(false);

  const isCurrentAdmin = user?.role === 'admin';

  const handleOpenRename = () => {
    setNewFamilyName(family?.name || '');
    setRenameOpen(true);
  };

  const handleSaveRename = async () => {
    const trimmed = (newFamilyName || '').trim();
    if (!trimmed) {
      toast.error('Family name cannot be empty');
      return;
    }
    if (trimmed === family?.name) {
      setRenameOpen(false);
      return;
    }
    setRenaming(true);
    try {
      await axios.patch(`${API}/family`, { name: trimmed }, { withCredentials: true });
      toast.success('Family renamed');
      setRenameOpen(false);
      await fetchFamily();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rename failed');
    } finally {
      setRenaming(false);
    }
  };

  const memberTier = (m) => {
    if (m.role === 'admin') return 'admin';
    if (m.role === 'guest') return 'viewer';
    return 'contributor';
  };

  const changeMemberTier = async (memberId, tier) => {
    try {
      await axios.put(`${API}/family/members/${memberId}/role`, { tier });
      toast.success('Role updated');
      await fetchFamily();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update role');
    }
  };

  const removeMember = async (memberId, name) => {
    if (!window.confirm(`Remove ${name} from your family?`)) return;
    try {
      await axios.delete(`${API}/family/members/${memberId}`);
      toast.success('Member removed');
      await fetchFamily();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove member');
    }
  };

  const renderMemberMenu = (member) => {
    if (!isCurrentAdmin || member.id === user?.id) return null;
    const tier = memberTier(member);
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-full text-stone-500 hover:text-stone-800"
            data-testid={`member-menu-${member.id}`}
            aria-label="Manage member"
          >
            <Settings2 className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuLabel className="text-xs text-stone-500">Change role</DropdownMenuLabel>
          {[
            { id: 'admin', label: 'Admin', desc: 'Full control', icon: Crown },
            { id: 'contributor', label: 'Contributor', desc: 'Post & comment', icon: Users },
            { id: 'viewer', label: 'Viewer', desc: 'Read only', icon: Eye },
          ].map(({ id, label, desc, icon: Icon }) => (
            <DropdownMenuItem
              key={id}
              disabled={tier === id}
              onSelect={() => changeMemberTier(member.id, id)}
              data-testid={`set-tier-${id}-${member.id}`}
              className="flex items-center gap-2"
            >
              <Icon className="w-4 h-4 text-teal-600" />
              <div className="flex-1">
                <div className="text-sm font-medium">{label}</div>
                <div className="text-xs text-stone-500">{desc}</div>
              </div>
              {tier === id && <Check className="w-4 h-4 text-teal-600" />}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-red-600 focus:text-red-700"
            onSelect={() => removeMember(member.id, member.name)}
            data-testid={`remove-member-${member.id}`}
          >
            <X className="w-4 h-4 mr-2" />
            Remove from family
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const fetchFamily = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/family`, { withCredentials: true });
      setFamily(response.data);
    } catch (error) {
      console.error('Failed to fetch family:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchInvites = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/family/invites`, { withCredentials: true });
      setInvites(response.data);
    } catch (error) {
      console.error('Failed to fetch invites:', error);
    }
  }, []);

  useEffect(() => {
    fetchFamily();
    fetchInvites();
  }, [fetchFamily, fetchInvites]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setInviting(true);
    try {
      const payload = {
        email: inviteEmail.trim(),
        role: inviteRole,
      };
      if (inviteRole === 'guest') {
        payload.access_level = inviteAccessLevel;
      } else {
        payload.access_level = 'full';
      }
      
      const response = await axios.post(`${API}/family/invite`, payload, { withCredentials: true });
      
      setLastInviteLink(response.data.invite_link);
      setLastInviteEmailSent(response.data.email_sent);
      setInviteEmail('');
      fetchInvites();
      
      if (response.data.email_sent) {
        toast.success('Invite email sent!');
      } else {
        toast.success('Invite created! Share the link below.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  const buildInviteLink = (token) => `${window.location.origin}/join?token=${token}`;

  const shareInviteForRow = async (invite) => {
    const link = buildInviteLink(invite.token);
    const text = `You're invited to join ${family?.name || 'our family'} on Kinsy — a private space to share photos, events and updates.`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Join ${family?.name || 'our family'} on Kinsy`,
          text,
          url: link,
        });
      } catch (err) {
        if (err?.name !== 'AbortError') toast.error('Share unavailable');
      }
    } else {
      window.location.href = `sms:?&body=${encodeURIComponent(`${text} ${link}`)}`;
    }
  };

  const handleCancelInvite = async (inviteId) => {
    if (!window.confirm('Cancel this invite?')) return;
    try {
      await axios.delete(`${API}/family/invites/${inviteId}`, { withCredentials: true });
      fetchInvites();
      toast.success('Invite cancelled');
    } catch (error) {
      toast.error('Failed to cancel invite');
    }
  };

  const copyInviteLink = async () => {
    try {
      await navigator.clipboard.writeText(lastInviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Link copied!');
    } catch {
      toast.error('Failed to copy');
    }
  };

  const shareInviteLink = async () => {
    const shareText = `You're invited to join the ${family?.name || ''} family on Kinsy — a private space to share photos, events and updates.`;
    const shareUrl = lastInviteLink;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Join ${family?.name || 'our family'} on Kinsy`,
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        // User cancelled — silent
        if (err?.name !== 'AbortError') {
          toast.error('Share unavailable');
        }
      }
    } else {
      // Desktop fallback — open SMS-style mail/text on user's default app
      const sms = `sms:?&body=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;
      window.location.href = sms;
    }
  };

  const canNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleBadge = (member) => {
    if (member.id === family?.admin_id) {
      return (
        <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
          <Crown className="w-3 h-3" />
          Admin
        </span>
      );
    }
    if (member.role === 'guest') {
      const accessIcon = member.access_level === 'view_only' ? <Eye className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />;
      const accessLabel = member.access_level === 'view_only' ? 'View Only' : 'Can Comment';
      return (
        <span className="flex items-center gap-1 text-xs text-violet-600 bg-violet-50 px-2 py-0.5 rounded-full">
          {accessIcon}
          {accessLabel}
        </span>
      );
    }
    return (
      <span className="text-xs text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full">
        Member
      </span>
    );
  };

  const isAdmin = family?.admin_id === user?.id;
  const coreMembers = family?.members?.filter(m => m.role !== 'guest') || [];
  const extendedFamily = family?.members?.filter(m => m.role === 'guest') || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <Header />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-20 md:pb-8" data-testid="family-page">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Family Header */}
        <div className="card-soft mb-6" data-testid="family-header">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-16 h-16 shrink-0 bg-teal-50 rounded-2xl flex items-center justify-center">
                <Users className="w-8 h-8 text-teal-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <h1
                    className="text-2xl font-semibold text-stone-900 tracking-tight leading-tight truncate"
                    data-testid="family-name"
                  >{family?.name}</h1>
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={handleOpenRename}
                      className="p-1.5 shrink-0 rounded-full hover:bg-stone-100 text-stone-500 hover:text-teal-700 transition"
                      aria-label="Edit family name"
                      data-testid="edit-family-name-btn"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <p className="text-stone-500 text-sm">
                  {coreMembers.length} {coreMembers.length === 1 ? 'member' : 'members'}
                  {extendedFamily.length > 0 && ` · ${extendedFamily.length} extended`}
                </p>
              </div>
            </div>
            {isAdmin && (
              <div className="flex gap-2 sm:ml-auto shrink-0 flex-wrap">
                <Button
                  asChild
                  variant="outline"
                  className="rounded-full border-stone-200"
                  data-testid="tree-btn"
                >
                  <Link to="/tree">
                    <Trees className="w-4 h-4 md:mr-2" />
                    <span className="hidden md:inline">Family tree</span>
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="rounded-full border-stone-200"
                  data-testid="moderation-btn"
                >
                  <Link to="/moderation">
                    <Flag className="w-4 h-4 md:mr-2" />
                    <span className="hidden md:inline">Moderation</span>
                  </Link>
                </Button>
                <Button
                  onClick={() => setInviteDialogOpen(true)}
                  className="bg-teal-600 hover:bg-teal-700 text-white rounded-full"
                  data-testid="invite-btn"
                >
                  <UserPlus className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Invite</span>
                </Button>
              </div>
            )}
            {!isAdmin && (
              <Button
                asChild
                variant="outline"
                className="rounded-full border-stone-200 sm:ml-auto shrink-0"
                data-testid="tree-btn"
              >
                <Link to="/tree">
                  <Trees className="w-4 h-4 md:mr-2" />
                  <span className="hidden md:inline">Family tree</span>
                </Link>
              </Button>
            )}
          </div>
        </div>

        {/* Core Family Members */}
        <div className="card-soft" data-testid="members-list">
          <h2 className="text-lg font-semibold text-stone-900 mb-4">Family Members</h2>
          <div className="space-y-3">
            {coreMembers.map(member => (
              <div
                key={member.id}
                className="flex items-center gap-4 p-3 bg-stone-50 rounded-xl"
                data-testid={`member-${member.id}`}
              >
                <Avatar className="h-12 w-12 border-2 border-teal-100">
                  <AvatarFallback className="bg-teal-600 text-white font-medium">
                    {getInitials(member.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-stone-900">{member.name}</p>
                    {getRoleBadge(member)}
                    {member.id === user?.id && (
                      <span className="text-xs text-stone-600 bg-stone-100 px-2 py-0.5 rounded-full">
                        You
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-stone-500">{member.email}</p>
                </div>
                {renderMemberMenu(member)}
              </div>
            ))}
          </div>
        </div>

        {/* Extended Family */}
        {extendedFamily.length > 0 && (
          <div className="card-soft mt-6" data-testid="extended-family-list">
            <div className="flex items-center gap-2 mb-4">
              <h2 className="text-lg font-semibold text-stone-900">Extended Family</h2>
              <Heart className="w-5 h-5 text-violet-400" />
            </div>
            <p className="text-sm text-stone-500 mb-4">
              Loved ones with limited access to share in your family moments
            </p>
            <div className="space-y-3">
              {extendedFamily.map(member => (
                <div
                  key={member.id}
                  className="flex items-center gap-4 p-3 bg-violet-50/50 rounded-xl"
                  data-testid={`extended-member-${member.id}`}
                >
                  <Avatar className="h-12 w-12 border-2 border-violet-100">
                    <AvatarFallback className="bg-violet-500 text-white font-medium">
                      {getInitials(member.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-stone-900">{member.name}</p>
                      {getRoleBadge(member)}
                      {member.id === user?.id && (
                        <span className="text-xs text-stone-600 bg-stone-100 px-2 py-0.5 rounded-full">
                          You
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-stone-500">{member.email}</p>
                  </div>
                  {renderMemberMenu(member)}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pending Invites */}
        {isAdmin && invites.filter(i => i.status === 'pending').length > 0 && (
          <div className="card-soft mt-6" data-testid="pending-invites">
            <h2 className="text-lg font-semibold text-stone-900 mb-4">Pending Invites</h2>
            <div className="space-y-3">
              {invites
                .filter(i => i.status === 'pending')
                .map(invite => (
                  <div
                    key={invite.id}
                    className="flex items-center gap-4 p-3 bg-amber-50 rounded-xl"
                    data-testid={`invite-${invite.id}`}
                  >
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                      <UserPlus className="w-5 h-5 text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-stone-900 truncate">{invite.email}</p>
                      <div className="flex items-center gap-2 text-sm text-stone-500">
                        <span>{invite.role === 'guest' ? 'Extended family' : 'Member'}</span>
                        {invite.email_sent && (
                          <span className="flex items-center gap-1 text-xs text-green-600">
                            <Mail className="w-3 h-3" /> Email sent
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => shareInviteForRow(invite)}
                      className="rounded-full h-8 px-3 text-teal-700 hover:text-teal-800 hover:bg-teal-50 text-xs font-medium"
                      data-testid={`share-invite-${invite.id}`}
                    >
                      <Share2 className="w-3.5 h-3.5 mr-1.5" />
                      Send via text
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleCancelInvite(invite.id)}
                      className="text-stone-400 hover:text-red-600 rounded-full h-8 w-8"
                      data-testid={`cancel-invite-${invite.id}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
            </div>
          </div>
        )}
      </main>

      <MobileNav />

      {/* Invite Dialog */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="rename-family-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-stone-900">Rename family</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="family-name-edit" className="text-sm text-stone-700">Family name</Label>
              <Input
                id="family-name-edit"
                value={newFamilyName}
                onChange={(e) => setNewFamilyName(e.target.value)}
                maxLength={60}
                autoFocus
                placeholder="e.g. The Smiths"
                data-testid="rename-family-input"
                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveRename(); }}
              />
              <p className="text-xs text-stone-500 mt-1">
                Multiple families can share the same name — Kinsy identifies your family by a private ID, not the label.
              </p>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                className="rounded-full"
                onClick={() => setRenameOpen(false)}
                data-testid="rename-family-cancel"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSaveRename}
                disabled={renaming}
                className="rounded-full bg-teal-600 hover:bg-teal-700 text-white"
                data-testid="rename-family-save"
              >
                {renaming ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>) : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={inviteDialogOpen} onOpenChange={(open) => {
        setInviteDialogOpen(open);
        if (!open) {
          setLastInviteLink('');
          setInviteRole('member');
          setInviteAccessLevel('can_comment');
        }
      }}>
        <DialogContent className="max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-stone-900">Invite to {family?.name}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleInvite} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="inviteEmail" className="text-stone-700">Email Address</Label>
              <Input
                id="inviteEmail"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="name@example.com"
                className="h-11 rounded-xl border-stone-200 focus:border-teal-500 focus:ring-teal-500"
                required
                data-testid="invite-email-input"
              />
            </div>

            {/* Role Selector */}
            <div className="space-y-3">
              <Label className="text-stone-700">Invite as</Label>
              <RadioGroup value={inviteRole} onValueChange={setInviteRole} className="space-y-2">
                <label
                  htmlFor="role-member"
                  className={`flex items-start gap-3 p-4 rounded-2xl border-2 cursor-pointer transition-colors ${
                    inviteRole === 'member' ? 'border-teal-500 bg-teal-50/50' : 'border-stone-200 hover:bg-stone-50'
                  }`}
                  data-testid="role-member-option"
                >
                  <RadioGroupItem value="member" id="role-member" className="mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-teal-600" />
                      <span className="font-medium text-stone-900">Family Member</span>
                    </div>
                    <p className="text-sm text-stone-500 mt-1">
                      Full access. Can post, comment, react, and create albums & events.
                    </p>
                  </div>
                </label>

                <label
                  htmlFor="role-guest"
                  className={`flex items-start gap-3 p-4 rounded-2xl border-2 cursor-pointer transition-colors ${
                    inviteRole === 'guest' ? 'border-violet-500 bg-violet-50/50' : 'border-stone-200 hover:bg-stone-50'
                  }`}
                  data-testid="role-guest-option"
                >
                  <RadioGroupItem value="guest" id="role-guest" className="mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Heart className="w-4 h-4 text-violet-500" />
                      <span className="font-medium text-stone-900">Extended Family</span>
                    </div>
                    <p className="text-sm text-stone-500 mt-1">
                      Loved ones who want to stay in the loop. Limited permissions.
                    </p>
                  </div>
                </label>
              </RadioGroup>
            </div>

            {/* Access Level (only for guests) */}
            {inviteRole === 'guest' && (
              <div className="space-y-3 pl-4 border-l-2 border-violet-200">
                <Label className="text-stone-700">Access level</Label>
                <RadioGroup value={inviteAccessLevel} onValueChange={setInviteAccessLevel} className="space-y-2">
                  <label
                    htmlFor="access-view"
                    className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                      inviteAccessLevel === 'view_only' ? 'border-violet-400 bg-violet-50' : 'border-stone-200'
                    }`}
                    data-testid="access-view-only-option"
                  >
                    <RadioGroupItem value="view_only" id="access-view" className="mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Eye className="w-4 h-4 text-stone-600" />
                        <span className="font-medium text-stone-900 text-sm">View Only</span>
                      </div>
                      <p className="text-xs text-stone-500 mt-0.5">
                        Can see posts, photos, and events. No comments or reactions.
                      </p>
                    </div>
                  </label>

                  <label
                    htmlFor="access-comment"
                    className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                      inviteAccessLevel === 'can_comment' ? 'border-violet-400 bg-violet-50' : 'border-stone-200'
                    }`}
                    data-testid="access-can-comment-option"
                  >
                    <RadioGroupItem value="can_comment" id="access-comment" className="mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-stone-600" />
                        <span className="font-medium text-stone-900 text-sm">Can Comment & React</span>
                      </div>
                      <p className="text-xs text-stone-500 mt-0.5">
                        Can view, comment, and react. Cannot create posts or albums.
                      </p>
                    </div>
                  </label>
                </RadioGroup>
              </div>
            )}

            <Button
              type="submit"
              disabled={inviting || !inviteEmail.trim()}
              className="w-full h-11 bg-teal-600 hover:bg-teal-700 text-white rounded-full font-medium"
              data-testid="send-invite-btn"
            >
              {inviting ? 'Sending...' : 'Send Invite'}
            </Button>
          </form>

          {lastInviteLink && (
            <div className="mt-4 p-4 bg-stone-50 rounded-2xl" data-testid="invite-link-section">
              {lastInviteEmailSent ? (
                <div className="flex items-start gap-2 mb-2">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-stone-700">Email sent! You can also share this link directly:</p>
                </div>
              ) : (
                <p className="text-sm text-stone-600 mb-2">
                  Share this invite link (email delivery not configured):
                </p>
              )}
              <div className="flex gap-2">
                <Input
                  value={lastInviteLink}
                  readOnly
                  className="flex-1 rounded-xl text-sm bg-white"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={copyInviteLink}
                  className="rounded-xl"
                  data-testid="copy-link-btn"
                >
                  {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 mt-3">
                <Button
                  type="button"
                  onClick={shareInviteLink}
                  className="flex-1 h-11 bg-teal-600 hover:bg-teal-700 text-white rounded-full font-medium"
                  data-testid="share-invite-btn"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  {canNativeShare ? 'Share via text or apps' : 'Send as text'}
                </Button>
                <Button
                  asChild
                  type="button"
                  variant="outline"
                  className="flex-1 h-11 rounded-full border-stone-200"
                  data-testid="sms-invite-btn"
                >
                  <a
                    href={`sms:?&body=${encodeURIComponent(`You're invited to join ${family?.name || 'our family'} on Kinsy: ${lastInviteLink}`)}`}
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Open Messages
                  </a>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
